import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  presignTenantLogoUpload,
  confirmTenantLogoUpload,
  type TenantLogoUploadConfirmResult,
} from "@/features/identity-hub/api/tenant-logo-upload.api";
import { putToSignedUrlWithProgress, stageMemoryPreflightUpload } from "@/features/preflight/api/uploads.api";
import { getTenantLogoMaxBytes, isAllowedTenantLogoType } from "@/features/identity-hub/lib/tenant-logo-upload-constraints";

interface UseTenantLogoUploadOptions {
  /** Tenant the upload is staged/confirmed under (the org being edited, or the acting admin's active tenant when provisioning a new one). */
  tenantId: string | null | undefined;
  onUploaded: (result: TenantLogoUploadConfirmResult) => void;
}

/**
 * Validate -> presign -> stage-or-PUT (with progress) -> confirm flow shared
 * by the Org drawer's settings tab and the New Org dialog, which previously
 * each carried a byte-for-byte copy of this orchestration plus its local
 * preview/progress state.
 */
export function useTenantLogoUpload({ tenantId, onUploaded }: UseTenantLogoUploadOptions) {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  /** Clears the preview and cancels any "in progress" UI state — used on error, on explicit remove, and when the caller (e.g. the drawer) switches to a different entity. */
  const clearPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setProgress(null);
  };

  const handleFileChange = async (file: File | undefined) => {
    if (!file || !tenantId) return;

    if (!isAllowedTenantLogoType(file.type)) {
      toast.error(t("iam.orgDrawer.settings.logoInvalidType", "Use PNG, JPEG, or WebP"));
      return;
    }
    const maxBytes = getTenantLogoMaxBytes(file.type);
    if (maxBytes != null && file.size > maxBytes) {
      toast.error(t("iam.orgDrawer.settings.logoTooLarge", "Logo file is too large"));
      return;
    }

    clearPreview();
    setPreviewUrl(URL.createObjectURL(file));
    setProgress(0);

    try {
      const presign = await presignTenantLogoUpload({
        tenantId,
        contentType: file.type,
        sizeBytes: file.size,
        originalName: file.name,
      });

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        await stageMemoryPreflightUpload({
          tenantId,
          uploadId: presign.uploadId,
          contentType: file.type,
          file,
        });
        setProgress(100);
      } else {
        await putToSignedUrlWithProgress(presign.uploadUrl, file, presign.requiredHeaders, {
          onProgress: setProgress,
        });
      }

      const confirmed = await confirmTenantLogoUpload({ tenantId, uploadId: presign.uploadId });
      onUploaded(confirmed);
      toast.success(t("iam.orgDrawer.settings.logoUploaded", "Logo uploaded"));
    } catch {
      toast.error(t("iam.orgDrawer.settings.logoUploadFailed", "Logo upload failed"));
      clearPreview();
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return {
    previewUrl,
    progress,
    isUploading: progress !== null,
    inputRef,
    handleFileChange,
    clearPreview,
  };
}
